import { Fragment } from "react";
import classes from "../../styles/Modal.module.css";
import Icon, { IconType } from "../Icon";
import {
  useAppSelector,
  useTexts,
  useBuyBonusConfig,
  useConfig,
} from "../../hooks";
import { selectButton } from "../../store/slices/buttons";
import Modal from "./Modal";
import {
  getClasses,
  formatCurrency,
  splitNewLines,
  doTranslation,
} from "../../utils";
import { MaxWin, SOCIAL_CASINO_LANG } from "../../commander/Types";

// TODO this can be refactored/optimized by spliting it into multiple components
const Help: React.FC<{
  shown?: boolean;
}> = ({ shown }) => {
  const { common, custom } = useTexts();
  const { stakes, currency, rtp, lang, maxWin, betBoost } = useAppSelector(
    (state) => {
      return {
        stakes: state.selectors.stake.possibleValues,
        currency: state.details.currency,
        rtp: state.details.rtp,
        lang: state.details.language,
        name: state.details.gameName,
        maxWin: state.details.maxWin,
        betBoost: state.betBoost,
      };
    }
  );
  const { maxWin: maxWinCfg } = useConfig();
  const autoplay = useAppSelector(selectButton.bind(null, "autoplay"));
  const turbo = useAppSelector(selectButton.bind(null, "turbo"));
  const minStake = Math.min(...stakes);
  const maxStake = Math.max(...stakes);
  // The RTP can be set directly in the game custom text files /which was the original way/,
  // or by setting the RTP value in the game state, at the game initialization
  const rtpText = rtp ? `${common.rtp}: ≈ ${rtp.value}%` : custom.rtp;
  const maxWinText =
    maxWin !== MaxWin.Brazil && maxWinCfg && maxWinCfg.value > 0
      ? `${common.maxwin}: ${maxWinCfg.value}x`
      : "";
  const { bonuses } = useBuyBonusConfig();
  const buybonusAvailable = Object.keys(bonuses).length > 0;
  const buybonusIconType =
    lang === SOCIAL_CASINO_LANG ? "BuyBonusSocial" : "BuyBonus";

  const BuyBonusDescription: React.FC<{
    title: string;
    description: any;
    available: boolean;
  }> = ({ title, description, available }) => {
    if (!available) return null;
    const vars = Object.keys(bonuses).reduce((acc, bonus) => {
      return acc.concat(
        bonuses[bonus]?.multiplier ?? "N/A",
        bonuses[bonus]?.rtp ?? "N/A"
      );
    }, []);

    return (
      <Fragment>
        <h1>{title}</h1>
        {Object.keys(description)
          .filter((key) => key.includes("buybonus"))
          .map((key) =>
            splitNewLines(doTranslation(key, custom, common, ...vars))
          )}
      </Fragment>
    );
  };

  const BetBoostDescription: React.FC<{
    title: string;
    description: any;
    available: boolean;
  }> = ({ title, description, available }) => {
    const state = useAppSelector(({ betBoost }) => betBoost);

    if (!available || !state) return null;

    return (
      <Fragment>
        <h1>{title}</h1>
        {Object.keys(description)
          .filter((key) => key.includes("descriptionbetboost"))
          .map((key) =>
            splitNewLines(
              doTranslation(key, custom, common, state?.rtp.toFixed(2))
            )
          )}
      </Fragment>
    );
  };

  const Instruction: React.FC<{
    hidden?: boolean;
    text: string | JSX.Element[];
    icon: IconType;
  }> = ({ hidden, text, icon }) => {
    if (hidden) return null;

    const ICONS_SIZES = {
      BetBoost: 95,
      PlayBoost: 95,
      Spin: 62,
      SelectStake: 65,
      Balance: 50,
      default: 53,
    } as any;

    const iconClass = `instructions-${icon.toLowerCase()}`;
    const _text = typeof text === "string" ? splitNewLines(text) : text;

    return (
      <div className={classes.instruction}>
        <div className={classes.icon}>
          <Icon
            type={icon}
            className={iconClass}
            size={ICONS_SIZES[icon] ?? ICONS_SIZES.default}
          />
        </div>
        <div className={classes.texts}>{_text}</div>
      </div>
    );
  };

  return (
    <Modal
      icon="Info"
      className={getClasses(
        classes,
        "help",
        "fixed",
        "fullscreen",
        "color text-white"
      )}
      shown={shown}
      title={common.help}
    >
      <div className={getClasses(classes, "content-main")}>
        <h1>{common.help1}</h1>
        {Object.keys(custom)
          .filter((key) => key.includes("help"))
          .map((key) => splitNewLines(custom[key]))}
        <BuyBonusDescription
          available={buybonusAvailable}
          title={common.buybonustitle}
          description={custom}
        />
        <BetBoostDescription
          available={!!betBoost && betBoost.value > 0}
          title={common.betboosttitle}
          description={{ ...common, ...custom }}
        />
        <h1>{common.help2}</h1>
        <Instruction icon="SelectStake" text={common.help3} />
        <Instruction icon="Spin" text={common.help4} />
        <Instruction
          hidden={!autoplay.available}
          icon="Play"
          text={common.help5}
        />
        <Instruction
          hidden={!turbo.available}
          icon="Lightning"
          text={common.turbospin}
        />
        <Instruction
          hidden={!buybonusAvailable}
          icon={buybonusIconType}
          text={common.buybonusbtn}
        />
        <Instruction
          hidden={!betBoost || betBoost.value === 0}
          icon={lang === "en_sc" ? "PlayBoost" : "BetBoost"}
          text={common.betboostbtn}
        />
        <h1>{common.help6}</h1>
        {splitNewLines(common.help7)}

        <p>
          {common.minStake} {formatCurrency(minStake, currency)}
        </p>
        <p>
          {common.maxStake} {formatCurrency(maxStake, currency)}
        </p>
        <p>{maxWinText}</p>
        <p>{rtpText}</p>
        <p>{common.rtpexplainer}</p>
        <h1>{common.help8}</h1>
        <Instruction icon="SoundOn" text={common.help9} />
        <Instruction icon="Info" text={common.help10} />
        <Instruction icon="FullscreenOn" text={common.help11} />
        <Instruction icon="Stake" text={common.help12} />
        <Instruction icon="SelectStake" text={common.help13} />
        <Instruction icon="Spin" text={common.help14} />
        <Instruction
          hidden={!autoplay.available}
          icon="Play"
          text={common.help15}
        />
        <Instruction
          hidden={!turbo.available}
          icon="Lightning"
          text={common.turbospininstruction}
        />
        <Instruction
          hidden={lang === SOCIAL_CASINO_LANG}
          icon="Balance"
          text={common.help16}
        />
        {splitNewLines(common.help17)}
        <div className={classes["game-version"]}>
          {common.version}: {custom.gameversion || "1.0.0"}
        </div>
      </div>
    </Modal>
  );
};

export default Help;
